import { useCallback, useContext, useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { FaPalette, FaEdit, FaTrash } from 'react-icons/fa';
import axios from 'axios';
import { Row, Col, Card, Button, Form, Table, Alert } from 'react-bootstrap';
import Spinner from '../Spinner';

import '../../styles/ColorManagement.css';
import { AuthContext } from '../../context/AuthContext';
import ColorPaletteModal from '../modals/ColorPickerModal';
import DeleteModal from '../modals/DeleteModal';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const ColorManagement = ({ embedded, styleNumber, companyCode, onSuccess, onCancel }) => {
  const { userData } = useContext(AuthContext);
  const [colors, setColors] = useState([]);
  const [, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true); // Page loading state
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    color_name: '',
    color_code: ''
  });

  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [colorToDelete, setColorToDelete] = useState(null);

  const fetchColors = useCallback(async () => {
    setPageLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/api/admin/colors/get-colors`, {
        params: { company_code: userData.company_code }
      });
      const data = response.data;
      if (data.success) {
        setColors(data.colors);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Error fetching colors');
    }
    setPageLoading(false);
  }, [userData.company_code]);

  useEffect(() => {
    fetchColors();
  }, [fetchColors]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = isEditing 
        ? `${BASE_URL}/api/admin/colors/update-colors/${editingId}`
        : `${BASE_URL}/api/admin/colors/add-colors`;
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          company_code: companyCode || userData.company_code
        }),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(isEditing ? 'Color updated successfully' : 'Color added successfully');
        fetchColors();
        if (!isEditing && embedded && styleNumber && companyCode) {
          // Assign to style
          let colorId = null;
          if (data.color && (data.color.color_id || data.color.id)) {
            colorId = data.color.color_id || data.color.id;
          } else if (data.id) {
            colorId = data.id;
          } else if (data.color_id) {
            colorId = data.color_id;
          }
          if (colorId) {
            const assignRes = await fetch(`${BASE_URL}/api/admin/styles/add-style-attributes`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                style_number: styleNumber,
                company_code: companyCode,
                type: 'colors',
                attribute_ids: [colorId]
              })
            });
            const assignData = await assignRes.json();
          } else {
            console.warn('Could not determine new color ID from response:', data);
          }
        }
        resetForm();
        if (onSuccess) onSuccess();
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Error saving color');
    }
    setLoading(false);
  };

  const handleEdit = (color) => {
    setIsEditing(true);
    setEditingId(color.color_id);
    setFormData({
      color_name: color.color_name,
      color_code: color.color_code
    });
  };

  const handleDelete = async (colorId) => {
    if (!colorId) {
      setError('Invalid color ID');
      return;
    }

    setColorToDelete(colorId);
    setShowDeleteModal(true);
  };

  const resetForm = () => {
    setFormData({ color_name: '', color_code: '' });
    setIsEditing(false);
    setEditingId(null);
  };

  return (
    <Container className="color-container">
      {/* Show spinner while page is loading */}
      {pageLoading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <Spinner text="Loading colors..." />
        </div>
      ) : (
        <>
          {/* Add new color */}
          <Card className="add-color">
            <Card.Header>
              <h5>Add New Color</h5>
            </Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}
              <Form onSubmit={handleSubmit} className="mb-3 add-color-form">
                <Row className="align-items-end">
                  <Col md={5} sm={12} className="mb-2">
                    <Form.Group controlId="colorName">
                      <Form.Label>Color Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter color name"
                        value={formData.color_name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            color_name: e.target.value,
                          })
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} sm={12} className="mb-2">
                    <Form.Group controlId="colorCode">
                      <Form.Label>Color Code</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter color code"
                        value={formData.color_code}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            color_code: e.target.value,
                          })
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3} sm={12} className="mb-2">
                    <Button
                      type="submit"
                      variant={isEditing ? "primary" : "primary"}
                      className="me-2"
                    >
                      {isEditing ? "Update" : "Add"}
                    </Button>
                    {isEditing && (
                      <Button variant="secondary" onClick={resetForm}>
                        Cancel
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
              <h6 className="or-h6"> OR </h6>
              {/* Color Palette Picker */}
              <div className="mb-2 color-picker">
                <FaPalette
                  className="action-icon me-2 text-primary"
                  onClick={() => setShowColorPicker(true)}
                  title="Select from Color Palette"
                  style={{ cursor: "pointer" }}
                />
                <span
                  className="text-primary"
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowColorPicker(true)}
                >
                  Select from Color Palette
                </span>
              </div>
            </Card.Body>
          </Card>

          {/* Existing color list */}
          <Card className="existing-color">
            <Card.Header>
              <h5>Existing Color List</h5>
            </Card.Header>
            <Card.Body>
              <Table
                responsive
                striped
                bordered
                hover
                size="sm"
                className="table-organized existing-color-table"
              >
                <thead>
                  <tr style={{ verticalAlign: "middle" }}>
                    <th style={{ minWidth: "30%" }}>Color Name</th>
                    <th style={{ minWidth: "20%" }}>Color Code</th>
                    <th style={{ width: "20%" }}>Preview</th>
                    <th style={{ width: "30%" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {colors.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center">
                        No colors found.
                      </td>
                    </tr>
                  ) : (
                    colors.map((item) => (
                      <tr
                        key={item.color_id || item.id}
                        style={{ verticalAlign: "middle", height: "34px" }}
                      >
                        <td
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item.color_name}
                        </td>
                        <td
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item.color_code}
                        </td>
                        <td>
                          <span
                            style={{
                              display: "inline-block",
                              width: "100%",
                              height: 30,
                              background: item.color_code,
                              border: "1px solid #ccc",
                            }}
                          ></span>
                        </td>
                        <td>
                          <span
                            className="action-icon me-2 text-warning"
                            style={{ cursor: "pointer", fontSize: "1.2rem" }}
                            title="Edit"
                            onClick={() => handleEdit(item)}
                          >
                            <FaEdit
                              style={{ cursor: "pointer", color: "#007bff" }}
                              size={13}
                            />
                          </span>
                          <span
                            className="action-icon text-danger"
                            style={{ cursor: "pointer", fontSize: "1.2rem" }}
                            title="Delete"
                            onClick={() =>
                              handleDelete(item.color_id || item.id)
                            }
                          >
                            <FaTrash
                              style={{ cursor: "pointer", color: "red" }}
                              size={13}
                            />
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Color Palette Modal */}
          <ColorPaletteModal
            showColorPicker={showColorPicker}
            setShowColorPicker={setShowColorPicker}
            formData={formData}
            setFormData={setFormData}
          />

          {/* Delete Confirmation Modal */}
          <DeleteModal
            show={showDeleteModal}
            onHide={() => {
              setShowDeleteModal(false);
              setColorToDelete(null);
            }}
            id={colorToDelete}
            deleteUrl="/api/admin/colors/delete-colors/:id"
            entityLabel="color"
            modalTitle="Delete Color"
            confirmMessage={
              <>
                Are you sure you want to <span className="text-danger">delete</span> this color?
              </>
            }
            onDeleteSuccess={() => {
              setSuccess('Color deleted successfully');
              fetchColors();
            }}
            onDeleteError={(error) => {
              setError(error.message || 'Error deleting color');
            }}
          />
        </>
      )}
    </Container>
  );
};

export default ColorManagement;
